// src/components/RunInAIButton.jsx
// Reusable "Run in AI" dropdown button.
// Used in DetailPanel (for entries) and UseSkillModal (for skills after variables filled).
//
// Props:
//   prompt     — the text to send (required)
//   model      — entry/skill model string, used for recommendation
//   type       — entry/skill type, used for recommendation fallback
//   size       — 'sm' | 'md' (default 'md')

import { useState, useEffect, useRef } from 'react';
import { AI_PLATFORMS, recommendPlatform, runInPlatform, isMobileDevice } from '../lib/runInAI';

export default function RunInAIButton({ prompt, model, entryType, size = 'md' }) {
  const [open, setOpen]           = useState(false);
  const [justRan, setJustRan]     = useState(null); // platform id that was just used
  const [wasCopied, setWasCopied] = useState(false); // true when mobile copy+open used
  const ref                       = useRef(null);
  const mobile                    = isMobileDevice();

  const recommended = recommendPlatform(model, entryType);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleRun = (platformId) => {
    if (!prompt?.trim()) return;
    const result = runInPlatform(platformId, prompt.trim());
    setJustRan(platformId);
    setWasCopied(result?.copied || false);
    setOpen(false);
    setTimeout(() => { setJustRan(null); setWasCopied(false); }, 3000);
  };

  const recommendedPlatform = AI_PLATFORMS.find(p => p.id === recommended);
  const otherPlatforms      = AI_PLATFORMS.filter(p => p.id !== recommended);

  const isHiggsfield = recommended === 'higgsfield';

  const btnPad  = size === 'sm' ? '6px 10px' : '9px 12px';
  const btnSize = size === 'sm' ? 11.5 : 12.5;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      {/* Main button — runs in recommended platform */}
      <button
        onClick={() => handleRun(recommended)}
        disabled={!prompt?.trim()}
        title={isHiggsfield ? `Copies prompt then opens Higgsfield` : `Run in ${recommendedPlatform?.name}`}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: justRan === recommended
            ? recommendedPlatform?.bg
            : 'var(--surface2)',
          border: `0.5px solid ${justRan === recommended ? recommendedPlatform?.border : 'var(--border2)'}`,
          borderRight: 'none',
          borderRadius: 'var(--radius-sm) 0 0 var(--radius-sm)',
          padding: btnPad,
          fontFamily: 'var(--font)',
          fontSize: btnSize,
          color: justRan === recommended ? recommendedPlatform?.color : 'var(--muted)',
          cursor: !prompt?.trim() ? 'not-allowed' : 'pointer',
          opacity: !prompt?.trim() ? 0.5 : 1,
          transition: 'all 0.12s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={e => {
          if (!prompt?.trim()) return;
          e.currentTarget.style.background = recommendedPlatform?.bg || 'var(--surface3)';
          e.currentTarget.style.borderColor = recommendedPlatform?.border || 'var(--border2)';
          e.currentTarget.style.color = recommendedPlatform?.color || 'var(--text)';
        }}
        onMouseLeave={e => {
          if (justRan === recommended) return;
          e.currentTarget.style.background = 'var(--surface2)';
          e.currentTarget.style.borderColor = 'var(--border2)';
          e.currentTarget.style.color = 'var(--muted)';
        }}
      >
        <span style={{ fontSize: btnSize + 1 }}>{recommendedPlatform?.icon}</span>
        {justRan === recommended
          ? (wasCopied ? '✓ Copied — paste when app opens' : '✓ Opened!')
          : `▶ Run in ${recommendedPlatform?.name}`
        }
        {(isHiggsfield || mobile) && justRan !== recommended && (
          <span style={{ fontSize: 10, opacity: 0.6 }}>
            {mobile ? '(copies prompt)' : '(copies & opens)'}
          </span>
        )}
      </button>

      {/* Chevron dropdown trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Run in a different AI tool"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: open ? 'var(--surface3)' : 'var(--surface2)',
          border: '0.5px solid var(--border2)',
          borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
          padding: size === 'sm' ? '6px 8px' : '9px 9px',
          cursor: 'pointer',
          color: 'var(--dim)',
          transition: 'all 0.12s',
          fontSize: 9,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface3)'; e.currentTarget.style.color = 'var(--muted)'; }}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.background = 'var(--surface2)'; e.currentTarget.style.color = 'var(--dim)'; } }}
      >
        {open ? '▴' : '▾'}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(100% + 6px)',
          left: 0,
          minWidth: 200,
          background: 'var(--surface)',
          border: '0.5px solid var(--border2)',
          borderRadius: 'var(--radius)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          zIndex: 9999,
          overflow: 'hidden',
        }}>
          {/* Section label */}
          <div style={{
            padding: '8px 14px 4px',
            fontSize: 10, color: 'var(--dim)',
            textTransform: 'uppercase', letterSpacing: '0.07em',
            borderBottom: '0.5px solid var(--border)',
          }}>
            {mobile ? 'Open & copy prompt' : 'Run in a different tool'}
          </div>
          {mobile && (
            <div style={{
              padding: '6px 14px 8px',
              fontSize: 11, color: 'var(--dim)',
              borderBottom: '0.5px solid var(--border)',
              lineHeight: 1.5,
            }}>
              Prompt copied to clipboard — just paste when the app opens
            </div>
          )}

          {otherPlatforms.map(platform => (
            <button
              key={platform.id}
              onClick={() => handleRun(platform.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '9px 14px',
                background: justRan === platform.id ? platform.bg : 'transparent',
                border: 'none', borderBottom: '0.5px solid var(--border)',
                cursor: 'pointer', fontFamily: 'var(--font)',
                fontSize: 13, color: justRan === platform.id ? platform.color : 'var(--text)',
                textAlign: 'left', transition: 'background 0.1s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = platform.bg; e.currentTarget.style.color = platform.color; }}
              onMouseLeave={e => {
                if (justRan !== platform.id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--text)';
                }
              }}
            >
              <span style={{ fontSize: 15, width: 20, textAlign: 'center', flexShrink: 0 }}>{platform.icon}</span>
              <span style={{ flex: 1 }}>{platform.name}</span>
              {platform.noPromptInject && (
                <span style={{ fontSize: 10, color: 'var(--dim)' }}>copies prompt</span>
              )}
              {justRan === platform.id && (
                <span style={{ fontSize: 11, color: platform.color }}>
                  {wasCopied ? '✓ copied' : '✓'}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
